import { useEffect, useRef, useState } from 'react';
import { FileType } from 'entities/Files/types';
import {
	boundingRegionsToRegions,
	getCellSourceId,
	getLabelSourceId,
	Region,
	SourcedBoundingRegion,
} from 'components/file-editor-components/file-viewer/region-types';

// Flattens every label/cell's boundingRegions into the flat Region[] shape FileViewer draws.
// Exported (not just used internally below) so a caller that just mutated fileDetails — e.g.
// deleting a label, which shifts every later label/cell's index-derived sourceId — can recompute
// the full set and hand it to FileViewer's resyncRegions to re-seed everything from scratch,
// rather than trying to patch individual stale-sourceId annotations one at a time.
export const computeRegionsFromFileDetails = (fileDetails: FileType): Region[] => {
	const serverBoundingRegions: SourcedBoundingRegion[] = (fileDetails.recognizedLabels || []).flatMap(
		(label, labelIndex) => {
			if (label.type === 'table') {
				return label.cells.flatMap((cell) =>
					(cell.boundingRegions || []).map((region) => ({
						...region,
						sourceId: getCellSourceId(labelIndex, cell.rowIndex, cell.columnIndex),
					})),
				);
			}
			return (label.boundingRegions || []).map((region) => ({
				...region,
				sourceId: getLabelSourceId(labelIndex),
			}));
		},
	);
	return boundingRegionsToRegions(serverBoundingRegions);
};

// Shared across every file-editor page (general/custom/generative): turns a file's
// recognizedLabels into drawable regions, and owns the selection state that FileViewer,
// LabelFields and TableFields all need to stay in sync with each other. Draw mode itself is
// purely internal to FileViewer — nothing else needs it, so it isn't tracked here.
export const useFileEditorRegions = (fileDetails: FileType | null | undefined) => {
	const [regions, setRegions] = useState<Region[]>([]);
	const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
	// FileViewer only ever reads `regions` once, to seed its initial set of annotations (see its
	// own serverRegionsInitializedRef) — every update after that is inert there. Without this
	// guard, every keystroke in a label/cell (a new fileDetails reference) re-flattens every
	// table's every cell's boundingRegions for nothing; for a 500-row table that's real, wasted
	// work on every render, so this only runs once, the first time real data arrives.
	const initializedRef = useRef(false);

	useEffect(() => {
		if (!fileDetails || initializedRef.current) return;
		initializedRef.current = true;
		setRegions(computeRegionsFromFileDetails(fileDetails));
	}, [fileDetails]);

	return {
		regions,
		setRegions,
		selectedSourceId,
		setSelectedSourceId,
	};
};

export default useFileEditorRegions;
